import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Users, Lock } from "lucide-react";
import CommunityCard from "@/components/CommunityCard";
import CommunityCreateModal from "@/components/CommunityCreateModal";
import { COMMUNITIES } from "@/constants/testIds";
import { useAuth } from "@/context/AuthContext";

export default function CommunitiesPage() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get("/communities");
    setRows(data);
  }, []);

  useEffect(() => { load(); }, [load]);

  // Card CTA: either joins, requests to join, or opens the community.
  const onPrimaryAction = async (c) => {
    if (!user) { toast.error("Please log in first."); return; }
    if (c.joined) return; // Link takes care of navigation
    const wantsRequest = c.visibility === "private" || c.join_policy === "request";
    try {
      if (wantsRequest) {
        await api.post(`/communities/${c.id}/join-requests`, { note: null });
        toast.success(`Request sent — ${c.name}`);
      } else {
        await api.post(`/communities/${c.id}/join`);
        toast.success(`Joined ${c.name}`);
      }
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update membership.");
    }
  };

  const publicRows = rows.filter((r) => r.visibility !== "private" || r.joined);
  const privateTeasers = rows.filter((r) => r.visibility === "private" && !r.joined);

  return (
    <div data-testid={COMMUNITIES.root} className="p-6 lg:p-8">
      <header className="sticky top-[calc(52px+env(safe-area-inset-top))] lg:top-0 -mx-6 lg:-mx-8 px-6 lg:px-8 py-4 glass border-b border-white/5 mb-6 z-10 flex items-start justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl tracking-tight">Communities</h1>
          <p className="text-sm text-zinc-500 mt-1">Find your people. Join the rooms that speak to you.</p>
        </div>
        <button
          type="button"
          onClick={() => setCreateOpen(true)}
          data-testid="communities-create-button"
          className="shrink-0 inline-flex items-center gap-1.5 bg-rose-600 hover:bg-rose-500 text-white font-semibold rounded-full px-4 py-2 text-xs"
        >
          <Plus size={14} /> Start one
        </button>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {publicRows.map((c) => (
          <CommunityCard key={c.id} community={c} onPrimaryAction={onPrimaryAction} />
        ))}
      </div>

      {privateTeasers.length > 0 && (
        <section className="mt-10">
          <div className="text-xs uppercase tracking-[0.35em] text-rose-400 mb-4 flex items-center gap-2">
            <Lock size={12} /> Members-only communities
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {privateTeasers.map((c) => (
              <CommunityCard key={c.id} community={c} onPrimaryAction={onPrimaryAction} />
            ))}
          </div>
        </section>
      )}

      {rows.length === 0 && (
        <div className="text-center text-sm text-zinc-500 py-16">
          <Users className="mx-auto mb-3 text-zinc-600" size={28} />
          No communities yet. Start the first one.
        </div>
      )}

      <CommunityCreateModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={load}
      />
    </div>
  );
}
